import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Grid2, Grid, Divider, useTheme } from '@mui/material';
import FlagIcon from '@mui/icons-material/Flag';
import { TbFlag3 } from "react-icons/tb";
import dayjs from 'dayjs';
import ResultMonteCarloSimulation from 'view/monteCarloSimulation/ResultMonteCarloSimulation';
import { useGetAllMonteCarloSimulationQuery } from "store/slices/apis/monteCarloSimulationSlice";
import moment from 'moment';

const MonteCarloSimulationReport = ({ projectId }) => {
    const theme = useTheme();
    const [isResultSimulation, setIsResultSimulatio] = useState(false);
    const [rowData, setRowData] = useState(null);
    const { data: allSimulationList, isLoading } = useGetAllMonteCarloSimulationQuery({
        projectId: projectId,
    });
    const [barWidth, setBarWidth] = useState(0);
    const barRef = useRef();

    useEffect(() => {
        if (!barRef.current) return;

        const resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                setBarWidth(entry.contentRect.width);
            }
        });

        resizeObserver.observe(barRef.current);

        return () => {
            if (barRef.current) {
                resizeObserver.unobserve(barRef.current);
            }
        };
    }, []);

    const rawResultData = allSimulationList?.data?.data[0]?.resultData;
    const resultData = rawResultData ? JSON.parse(rawResultData) : {};

    const getGradientColor = (simDateStr) => {
        if (!simDateStr || !resultData?.PlanFinishDate) return '#D9D9D9';

        const simDate = moment(simDateStr);
        const planDate = moment(resultData.PlanFinishDate);

        return simDate.isAfter(planDate)
            ? `linear-gradient(90deg, ${theme?.customSettings?.themeColor}, ${theme?.customSettings?.themeColor})`
            : `linear-gradient(90deg, ${theme?.customSettings?.themeColor}, ${theme?.customSettings?.themeColor})`;
    };

    const milestoneData = [
        {
            label: `P ${Math.min(allSimulationList?.data?.data[0]?.minimum || 0, 100)}`,
            date: resultData?.MinimumCopletionDate
                ? moment(resultData.MinimumCopletionDate).format('DD MMM')
                : '',
            description: resultData?.MinimumCopletionDate
                ? `${Math.min(allSimulationList.data.data[0].minimum, 100)}% chances of finishing the project on ${moment(resultData.MinimumCopletionDate).format('DD/MM/YYYY')}`
                : '',
            color: getGradientColor(resultData?.MinimumCopletionDate),
            iconBg: '#006045',
        },
        {
            label: `P ${Math.min(allSimulationList?.data?.data[0]?.mostLikely || 0, 100)}`,
            date: resultData?.MostLikelyCopletionDate
                ? moment(resultData.MostLikelyCopletionDate).format('DD MMM')
                : '',
            description: resultData?.MostLikelyCopletionDate
                ? `${Math.min(allSimulationList.data.data[0].mostLikely, 100)}% chances of finishing the project on ${moment(resultData.MostLikelyCopletionDate).format('DD/MM/YYYY')}`
                : '',
            color: getGradientColor(resultData?.MostLikelyCopletionDate),
            iconBg: '#006045',
        },
        {
            label: `P ${Math.min(allSimulationList?.data?.data[0]?.maximum || 0, 100)}`,
            date: resultData?.MaximumCopletionDate
                ? moment(resultData.MaximumCopletionDate).format('DD MMM')
                : '',
            description: resultData?.MaximumCopletionDate
                ? `${Math.min(allSimulationList.data.data[0].maximum, 100)}% chances of finishing the project on ${moment(resultData.MaximumCopletionDate).format('DD/MM/YYYY')}`
                : '',
            color: getGradientColor(resultData?.MaximumCopletionDate),
            iconBg: '#006045',
        }
    ];

    const handleResultView = () => {
        const resultMonteCaro = allSimulationList?.data?.data[0] || {}
        setIsResultSimulatio((prev) => {
            const newState = !prev;
            setRowData(newState ? resultMonteCaro : null);
            return newState;
        });
    }
    return (
        <>
            <Box sx={{ border: '1px solid #ddd', borderRadius: 2, p: 2, bgcolor: '#fff', height: "100%" }}>
                {/* Header */}
                <Typography sx={{ fontWeight: 500, fontSize: "18px", lineHeight: "100%", letterSpacing: "0.4px", color: "#182A4E" }} >
                    Monte - Carlo Simulation Report
                </Typography>
                <Typography sx={{ mt: 1, fontWeight: 500, fontSize: "14px", lineHeight: "100%", letterSpacing: "0.4px", color: '#182A4E', opacity: "0.5" }} >
                    Quantifying Project Risks Through Simulation
                </Typography>

                <Grid container sx={{ mt: 2, border: '1px solid #D1D5DB', borderRadius: 1, textAlign: 'center', bgcolor: '#F3F3F3' }}>
                    <Grid item xs={2} sx={{ p: 1, borderRight: '1px solid #D9D9D9' }}></Grid>

                    <Grid item xs={2} sx={{ p: 1, borderRight: '1px solid #D9D9D9' }}>
                        <Typography sx={{ fontWeight: 500, fontSize: "14px", lineHeight: "100%", color: "#2C2C2C" }}>
                            {milestoneData[0]?.label || ""}
                        </Typography>
                    </Grid>

                    <Grid item xs={4} sx={{ p: 1, borderRight: '1px solid #D9D9D9' }}>
                        <Typography sx={{ fontWeight: 500, fontSize: "14px", lineHeight: "100%", color: "#2C2C2C" }}>
                            Plan Finish Date
                        </Typography>
                    </Grid>

                    <Grid item xs={2} sx={{ p: 1, borderRight: '1px solid #D9D9D9' }}>
                        <Typography sx={{ fontWeight: 500, fontSize: "14px", lineHeight: "100%", color: "#2C2C2C" }}>
                            {milestoneData[1]?.label || ""}
                        </Typography>
                    </Grid>

                    <Grid item xs={2} sx={{ p: 1 }}>
                        <Typography sx={{ fontWeight: 500, fontSize: "14px", lineHeight: "100%", color: "#2C2C2C" }}>
                            {milestoneData[2]?.label || ""}
                        </Typography>
                    </Grid>
                </Grid>


                {/* Timeline with vertical line */}
                <Box sx={{ position: 'relative', mt: 3, pb: 2, ml: 1 }}>
                    {/* Vertical dotted line */}
                    <Divider orientation="vertical" flexItem sx={{
                        position: 'absolute',
                        left: '50%',
                        top: 0,
                        height: 'calc(100% - 40px)',
                        borderStyle: 'dashed',
                        borderColor: '#9CA3AF',
                    }} />

                    {/* Date label on line */}
                    <Typography variant="caption" sx={{ position: 'absolute', left: 'calc(45% - 10px)', top: -20 }}>
                        {resultData?.PlanFinishDate ? moment(resultData?.PlanFinishDate).format('D MMMM, YYYY') : ""}
                    </Typography>

                    {/* Milestones */}
                    <Grid2 container direction="column" spacing={2}>
                        {milestoneData.map((item, idx) => {
                            const percentage = parseInt(item.label.replace(/[^\d]/g, ''), 10);
                            const barsWidth = `${Math.min(percentage, 100)}%`;
                            return (
                                <Grid2 item key={idx}>
                                    <Box sx={{ display: 'flex', alignItems: 'start', gap: 2, mt: 1 }}>
                                        <Box
                                            ref={barRef}
                                            sx={{
                                                background: item.color,
                                                borderRadius: "4px",
                                                px: 2,
                                                py: 1,
                                                color: '#fff',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                minWidth: '100px',
                                                maxWidth: '100%',
                                                width: barsWidth,
                                            }}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Typography sx={{ fontWeight: 600, fontSize: "12px", lineHeight: "100%" }}>{item.label}</Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: "5px" }}>
                                                    <TbFlag3 sx={{ fontSize: 14 }} />
                                                    <Typography sx={{ fontWeight: 400, fontSize: "10px", lineHeight: "100%" }}>
                                                        {item.date}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            {barWidth < 180 && item.description && (
                                                <Typography
                                                    sx={{
                                                        mt: 0.5,
                                                        fontWeight: 500,
                                                        fontSize: '11px',
                                                        lineHeight: '100%',
                                                        color: '#fff',
                                                        // opacity: '0.43',
                                                    }}
                                                >
                                                    {item.description}
                                                </Typography>
                                            )}

                                            {barWidth >= 180 && item.description && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontWeight: 500,
                                                        fontSize: '11px',
                                                        lineHeight: '100%',
                                                        mt: 1,
                                                        whiteSpace: 'pre-line',
                                                    }}
                                                >
                                                    {item.description}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Grid2>
                            )
                        })}
                    </Grid2>

                    {/* Footer */}
                    <Typography sx={{ fontWeight: 500, fontSize: "11px", lineHeight: "100%", color: "#000000", opacity: "0.5", mt: 1 }}>
                        {allSimulationList?.data?.data[0]?.updateDate ? "Last updated on " + moment(allSimulationList?.data?.data[0]?.updateDate).format('D MMMM, YYYY') : ""}
                    </Typography>

                    <Typography
                        variant="body2"
                        onClick={handleResultView}
                        sx={{ pt: 11, color: "#182A4E", cursor: "pointer", textDecoration: "underline", fontWeight: 500, fontSize: "15px", lineHeight: "100%", letterSpacing: "0.4px" }}
                    >
                        View More...
                    </Typography>
                </Box>
            </Box>
            {isResultSimulation &&
                <ResultMonteCarloSimulation open={isResultSimulation} cancel={handleResultView} rowData={rowData} publish={false} />
            }
        </>
    );
};

export default MonteCarloSimulationReport;
